import React, { useState } from "react";
import { useExpansionRead } from "hooks";
import { QueryKeys, queryClient } from "queryClient";
import { Expansion } from "types";
import { CustomButton } from "core";
import { PDFFileIcon } from "modules/icons";
import { getProductListByExpansion } from "modules/products/api";
import { ExportLanguage } from "utils/print/expansionCardListPdf";
import utils from "utils";

type ExpansionCardListPdfExportProps = {
  /**
   * Edition, die exportiert werden soll. Ohne Angabe wird sie aus dem
   * Routen-Parameter gelesen (Editionsseite). Auf der globalen Kartenansicht gibt
   * es keinen solchen Parameter - dort kommt die Edition aus dem Filter und wird
   * hier übergeben.
   */
  expansion?: Expansion | null;
};

export const ExpansionCardListPdfExport: React.FC<
  ExpansionCardListPdfExportProps
> = ({ expansion: expansionFromProps }) => {
  const { expansion: expansionFromRoute, isLoading: isLoadingFromRoute } =
    useExpansionRead();

  const expansion = expansionFromProps ?? expansionFromRoute;
  const isLoading = expansionFromProps ? false : isLoadingFromRoute;

  const [isGenerating, setIsGenerating] = useState(false);

  const handleExport = async (language: ExportLanguage) => {
    if (!expansion) return;

    const expansionID = expansion.id;

    setIsGenerating(true);

    try {
      // Volle Editionsliste aus dem Cache wiederverwenden (die Seite lädt sie
      // bereits) und unabhängig vom Seitenfilter clientseitig auf aktive Karten
      // reduzieren.
      const allProducts = await queryClient.ensureQueryData({
        queryKey: [QueryKeys.ExpansionProducts, expansionID],
        queryFn: () => getProductListByExpansion(expansionID),
      });

      const activeProducts = allProducts.filter(
        (product) => product.isProductActive,
      );

      utils.print.generateExpansionCardListPdf({
        products: activeProducts,
        expansion,
        language,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const disabled = isLoading || !expansion;

  return (
    <>
      <CustomButton
        text="PDF (Deutsch)"
        size="small"
        iconBefore={<PDFFileIcon />}
        onClick={() => handleExport("de")}
        disabled={disabled}
        loading={isGenerating}
        loadingTime={0}
        styleWrapper={{ marginLeft: 33 }}
      />
      <CustomButton
        text="PDF (Englisch)"
        size="small"
        iconBefore={<PDFFileIcon />}
        onClick={() => handleExport("en")}
        disabled={disabled}
        loading={isGenerating}
        loadingTime={0}
        styleWrapper={{ marginLeft: 11 }}
      />
    </>
  );
};
